import type {
  AnalyticsConfig,
  PentictonEventsTracking,
  PageViewData,
  UserProperties,
} from './types';
import { AnalyticsProvider } from './providers/base';
import { GoogleAnalyticsProvider } from './providers/google';
import { PlausibleProvider } from './providers/plausible';
import { NoopProvider } from './providers/noop';

/**
 * Analytics service that manages multiple providers
 */
class AnalyticsService {
  private provider: AnalyticsProvider | null = null;
  private config: AnalyticsConfig;
  private initialized: boolean = false;

  constructor() {
    this.config = this.getConfig();
  }

  private getConfig(): AnalyticsConfig {
    const isDevelopment = import.meta.env.DEV;
    const provider =
      (import.meta.env.VITE_ANALYTICS_PROVIDER as AnalyticsConfig['provider']) || 'none';

    // Allow overriding consent requirement via environment variable
    // Default to true for GDPR/cookie compliance even without analytics
    const consentRequired =
      import.meta.env.VITE_CONSENT_REQUIRED === 'false' ? false : true;

    return {
      provider,
      trackingId: import.meta.env.VITE_GA_TRACKING_ID,
      domain: import.meta.env.VITE_PLAUSIBLE_DOMAIN || 'penticton.dev',
      apiHost: import.meta.env.VITE_PLAUSIBLE_API_HOST || 'https://plausible.io',
      enabled: !isDevelopment && provider !== 'none',
      debug: isDevelopment,
      consentRequired,
      consentGranted: false,
    };
  }

  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    try {
      // Create the appropriate provider
      switch (this.config.provider) {
        case 'google':
          if (!this.config.trackingId) {
            console.warn('Google Analytics tracking ID not configured');
            this.provider = new NoopProvider(false, this.config.debug);
          } else {
            this.provider = new GoogleAnalyticsProvider(
              this.config.trackingId,
              this.config.enabled,
              this.config.debug,
            );
          }
          break;

        case 'plausible':
          if (!this.config.domain) {
            console.warn('Plausible domain not configured');
            this.provider = new NoopProvider(false, this.config.debug);
          } else {
            this.provider = new PlausibleProvider(
              this.config.domain,
              this.config.apiHost,
              this.config.enabled,
              this.config.debug,
            );
          }
          break;

        case 'none':
        default:
          this.provider = new NoopProvider(false, this.config.debug);
          break;
      }

      await this.provider.initialize();
      this.initialized = true;

      if (this.config.debug) {
        console.log('Analytics initialized with provider:', this.config.provider);
      }
    } catch (error) {
      console.error('Failed to initialize analytics:', error);
      // Fallback to noop provider
      this.provider = new NoopProvider(false, this.config.debug);
    }
  }

  /**
   * Track a custom event
   */
  trackEvent<T extends keyof PentictonEventsTracking>(
    eventName: T,
    properties: PentictonEventsTracking[T],
  ): void {
    if (!this.provider) {
      console.warn('Analytics not initialized');
      return;
    }

    this.provider.trackEvent(eventName, properties);
  }

  /**
   * Track a page view
   */
  trackPageView(path: string, title?: string, userId?: string): void {
    if (!this.provider) {
      console.warn('Analytics not initialized');
      return;
    }

    const data: PageViewData = {
      page_title: title || document.title,
      page_location: `${window.location.origin}${path}`,
      user_id: userId,
    };

    this.provider.trackPageView(data);
  }

  /**
   * Set user properties
   */
  setUserProperties(properties: UserProperties): void {
    if (!this.provider) {
      console.warn('Analytics not initialized');
      return;
    }

    this.provider.setUserProperties(properties);
  }

  /**
   * Update consent status
   */
  setConsent(granted: boolean): void {
    this.config.consentGranted = granted;

    if (this.provider) {
      this.provider.setConsent(granted);
    }
  }

  /**
   * Get current configuration
   */
  getCurrentConfig(): AnalyticsConfig {
    return { ...this.config };
  }

  /**
   * Check if consent is required
   * Note: We require consent even without analytics for cookie compliance
   */
  isConsentRequired(): boolean {
    return this.config.consentRequired;
  }

  /**
   * Check if consent is granted
   */
  isConsentGranted(): boolean {
    return this.config.consentGranted;
  }
}

// Create singleton instance
export const analytics = new AnalyticsService();

// Export types
export type { PentictonEventsTracking, AnalyticsConfig } from './types';

// Initialize analytics on module load
analytics.initialize().catch(console.error);

export default analytics;
